import { ArrowUpCircleIcon, CubeIcon } from '@heroicons/react/24/outline';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { CatalogueAppItem } from 'softcat:components/catalogue-app-item.tsx';
import { CatalogueWidgetModal } from 'softcat:components/catalogue-widget-modal.tsx';
import { CertificateStatus } from 'softcat:components/certificate-status.tsx';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { BadgeState } from 'softcat:config/badge-state.ts';
import { CatalogueState } from 'softcat:config/catalogue-state.ts';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { CatalogueService } from 'softcat:services/catalogue.ts';
import { SoftcatApiPackage, SoftcatApiWidget } from 'softcat:types/softcat-api.ts';
import { CatalogueUtils } from 'softcat:utils/catalogue.ts';
import useSWR from 'swr';
import * as z from 'zod';

export const CataloguesEdit = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const navigate = useNavigate();
    const { name = '', version = '' } = useParams<{ name: string; version: string }>();
    const [submitting, setSubmitting] = useState(false);
    const [onEditError, setOnEditError] = useState('');
    const [showAddPackage, setShowAddPackage] = useState(false);
    const [formValues, setFormValues] = useState({ name: '', packages: [] as SoftcatApiPackage[] | undefined, status: '' as CatalogueState });
    const sortableRepeaterReference = useRef<{ prepend: (item: unknown) => void }>(null);

    const EditCatalogueSchema = z.object({
        name: z.string().regex(/^[\s\w().-]+$/, { message: intl.formatMessage({ id: 'CATALOGUES_FORM_NAME_VALIDATION' }) }),
        packages: z.array(z.any()).optional(),
        status: z.nativeEnum(CatalogueState),
    });

    const {
        data: catalogueData,
        error: catalogueDataError,
        isLoading: catalogueDataIsLoading,
    } = useSWR({ key: `${activeTerritory}-CatalogueServiceFindOne`, name, version }, CatalogueService.findOne);

    const soonestAppExpiryDate = useMemo(() => {
        return CatalogueUtils.findSoonestWidgetExpiry(catalogueData);
    }, [catalogueData]);

    useEffect(() => {
        if (catalogueData) {
            setFormValues({
                name: catalogueData.name,
                packages: catalogueData.packages,
                status: catalogueData.status as CatalogueState,
            });
        }
    }, [catalogueData]);

    const onSubmit = async (values: z.infer<typeof EditCatalogueSchema>) => {
        setSubmitting(true);
        setOnEditError('');

        if (catalogueData) {
            try {
                await CatalogueService.update({
                    data: {
                        ...catalogueData,
                        name: values.name,
                        packages:
                            values.packages?.map((widget, index) => {
                                return {
                                    ...widget,
                                    position: index,
                                };
                            }) || [],
                        status: values.status,
                    },
                    name: catalogueData.name,
                    version: catalogueData.version,
                });

                setSubmitting(false);

                return navigate('/catalogues');
            } catch (error) {
                setSubmitting(false);
                setOnEditError((error as Error).message);
            }
        }
    };

    const attachApp = (widget: SoftcatApiWidget) => {
        if (sortableRepeaterReference.current) {
            sortableRepeaterReference.current.prepend({
                entitlements: [],
                visibleInTray: true,
                widget,
            });
        }

        setShowAddPackage(false);
    };

    return (
        <Form<z.infer<typeof EditCatalogueSchema>> defaultValues={formValues} onSubmit={onSubmit} resolver={zodResolver(EditCatalogueSchema)}>
            <div className="space-y-3 px-4">
                <Form.Header
                    description={<FormattedMessage id={'CATALOGUES_EDIT_PAGE_DESCRIPTION'} />}
                    title={<FormattedMessage id={'CATALOGUES_EDIT_PAGE_HEADING'} />}
                />

                {catalogueDataError && (
                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'CATALOGUES_EDIT_PAGE_ERROR_LOADING_DATA'} />
                    </p>
                )}

                {catalogueDataIsLoading && <Spinner />}

                {catalogueData && (
                    <>
                        <Form.Input label={<FormattedMessage id={'CATALOGUES_FORM_NAME_LABEL'} />} name={'name'} />

                        <Form.StaticValue label={<FormattedMessage id={'CATALOGUES_FORM_VERSION_LABEL'} />} value={catalogueData.version} />

                        <Form.RadioGroup
                            label={<FormattedMessage id={'CATALOGUES_FORM_STATUS_LABEL'} />}
                            name={'status'}
                            options={Object.values(CatalogueState).map((state) => {
                                return { label: state, value: state };
                            })}
                        />

                        <Form.StaticValue label={<FormattedMessage id={'CATALOGUES_FORM_CONFIG_VERSION_LABEL'} />} value={catalogueData.configVersion} />

                        <Form.StaticValue
                            label={<FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_APP_CERTIFICATE_EXPIRY_LABEL'} />}
                            value={<CertificateStatus certificateExpiry={soonestAppExpiryDate} />}
                        />

                        <Form.StaticValue
                            label={<FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_CATALOGUE_CERTIFICATE_EXPIRY_LABEL'} />}
                            value={<CertificateStatus certificateExpiry={catalogueData.notAfter} />}
                        />

                        <Form.StaticBadges
                            label={<FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_LOCATIONS_LABEL'} />}
                            status={BadgeState.WARNING}
                            values={catalogueData.locations}
                        />

                        <Form.SortableList
                            label={
                                <span className={'mb-2 inline-flex flex-col gap-1'}>
                                    <FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_APPS_LABEL'} />
                                    <button
                                        className={'flex items-center text-xs text-indigo-600 outline-0'}
                                        data-testid={`add-catalogue-app`}
                                        onClick={() => {
                                            setShowAddPackage(true);
                                        }}
                                        type={'button'}
                                    >
                                        <CubeIcon className={'mr-2 inline-block size-4'} />
                                        <FormattedMessage id={'CATALOGUES_EDIT_PAGE_ATTACH_APP'} />
                                    </button>
                                </span>
                            }
                            name={'packages'}
                            ref={sortableRepeaterReference}
                            renderField={({ field, index, name, remove }) => {
                                return <CatalogueAppItem index={index} item={field as SoftcatApiPackage} name={name} remove={remove} />;
                            }}
                        />

                        <Form.StaticValue
                            label={<FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_LAST_PUBLISHED_LABEL'} />}
                            value={
                                <span className={'flex items-center gap-1 text-violet-400'}>
                                    {catalogueData.lastPublishedDate ? (
                                        <>
                                            <ArrowUpCircleIcon className={'inline-block size-4'} />
                                            <span data-testid={'published-details'}>
                                                <FormattedMessage
                                                    id={'CATALOGUES_EDIT_PAGE_FORM_LAST_PUBLISHED_BY'}
                                                    values={{
                                                        date: new Date(catalogueData.lastPublishedDate).toLocaleString(undefined, {
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            hour12: false,
                                                            minute: '2-digit',
                                                            month: 'short',
                                                            second: '2-digit',
                                                            weekday: 'short',
                                                            year: 'numeric',
                                                        }),
                                                        username: catalogueData.lastPublishedBy,
                                                    }}
                                                />
                                            </span>
                                        </>
                                    ) : (
                                        <FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_UNPUBLISHED'} />
                                    )}
                                </span>
                            }
                        />

                        <Form.StaticValue
                            label={<FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_LOCATION_URL'} />}
                            value={
                                catalogueData.catalogueLocation ? (
                                    <a
                                        className={'inline-flex items-center gap-2 transition-colors hover:text-indigo-600'}
                                        href={catalogueData.catalogueLocation}
                                        rel="noreferrer"
                                        target={'_blank'}
                                    >
                                        <ArrowDownTrayIcon className={'inline-block size-4'} /> {catalogueData.catalogueLocation}
                                    </a>
                                ) : (
                                    '-'
                                )
                            }
                        />

                        <Form.Footer
                            isSubmitting={submitting}
                            onCancel={() => {
                                return navigate('/catalogues');
                            }}
                            submitError={onEditError && <FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onEditError }} />}
                            submitText={<FormattedMessage id={'BUTTON_UPDATE'} />}
                        />
                    </>
                )}
            </div>
            <CatalogueWidgetModal
                handleClose={() => {
                    setShowAddPackage(false);
                }}
                handleConfirm={(widget) => {
                    attachApp(widget);
                }}
                isVisible={showAddPackage}
            />
        </Form>
    );
};
